import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { EnvironmentService } from '@nomoh-monorepo/shared-data-access-environment';
import { IPagerSettings } from '@nomoh-monorepo/shared-types';
import { firstValueFrom } from 'rxjs';
import { IUsersResponse, UserDto, UserFiltroDto } from './models';

@Injectable({ providedIn: 'root' })
export class UsersDataService {
  private http = inject(HttpClient);
  private environment = inject(EnvironmentService);
  private baseUrl = `${this.environment.getApiUrl('nomoh')}/pessoa/admin`; // Endpoint da API de usuários

  // Estado com Signals (NOSSO PADRÃO)
  private _users = signal<UserDto[]>([]);
  private _loading = signal<boolean>(false);
  private _erro = signal<string | null>(null);
  private _paginacao = signal<IPagerSettings>({
    total: 0,
    offSet: 0,
    limit: 20,
    param: '',
    ordenacao: 0,
  });
  private _filtros = signal<Omit<UserFiltroDto, 'offSet' | 'limit'>>({});

  // Signals públicos
  $users = this._users.asReadonly();
  $loading = this._loading.asReadonly();
  $erro = this._erro.asReadonly();
  $paginacao = this._paginacao.asReadonly();
  $filtros = this._filtros.asReadonly();

  // Computed signals
  $temProxPagina = computed(() => {
    const pag = this.$paginacao();
    const offSet = pag.offSet ?? 0;
    const limit = pag.limit ?? 20;
    const total = pag.total ?? 0;
    return offSet + limit < total;
  });

  $temPagAnterior = computed(() => {
    const offSet = this.$paginacao().offSet ?? 0;
    return offSet > 0;
  });

  $paginaAtual = computed(() => {
    const pag = this.$paginacao();
    const offSet = pag.offSet ?? 0;
    const limit = pag.limit ?? 20;
    return Math.floor(offSet / limit) + 1;
  });

  $totalPaginas = computed(() => {
    const pag = this.$paginacao();
    const total = pag.total ?? 0;
    const limit = pag.limit ?? 20;
    return Math.ceil(total / limit);
  });

  $infoTotal = computed(() => {
    const pag = this.$paginacao();
    const offSet = pag.offSet ?? 0;
    const limit = pag.limit ?? 20;
    const total = pag.total ?? 0;
    const inicio = offSet + 1;
    const fim = Math.min(offSet + limit, total);
    return `${inicio}-${fim} de ${total}`;
  });

  // Métodos de estado
  setUsers(response: IUsersResponse) {
    if (response.success) {
      this._users.set(response.data);
      this._paginacao.set(response.pagerSettings);
    } else {
      this.setErro(response.errorMessage);
    }
  }

  setLoading(loading: boolean) {
    this._loading.set(loading);
  }

  setErro(erro: string | null) {
    this._erro.set(erro);
  }

  setFiltros(filtros: Partial<Omit<UserFiltroDto, 'offSet' | 'limit'>>) {
    this._filtros.update((current) => ({ ...current, ...filtros }));
  }

  // Operações HTTP + Estado (API real: POST /userAdmin/findAll)
  async carregarUsers(opcoes?: {
    offSet?: number;
    limit?: number;
  }): Promise<void> {
    this.setLoading(true);
    this.setErro(null);

    try {
      const pag = this.$paginacao();
      const filtroDto: UserFiltroDto = {
        ...this.$filtros(),
        offSet: opcoes?.offSet ?? pag.offSet ?? 0,
        limit: opcoes?.limit ?? pag.limit ?? 20,
        param: pag.param ?? '',
        ordenacao: pag.ordenacao ?? 0,
      };

      const response = await firstValueFrom(
        this.http.post<IUsersResponse>(`${this.baseUrl}/findAll`, filtroDto)
      );

      this.setUsers(response);
    } catch (error) {
      this.setErro('Erro ao carregar usuários');
      console.error(error);
    } finally {
      this.setLoading(false);
    }
  }

  async proximaPagina(): Promise<void> {
    if (this.$temProxPagina()) {
      const pag = this.$paginacao();
      const novoPagOffset = (pag.offSet ?? 0) + (pag.limit ?? 20);
      await this.carregarUsers({ offSet: novoPagOffset });
    }
  }

  async paginaAnterior(): Promise<void> {
    if (this.$temPagAnterior()) {
      const pag = this.$paginacao();
      const novoPagOffset = Math.max(0, (pag.offSet ?? 0) - (pag.limit ?? 20));
      await this.carregarUsers({ offSet: novoPagOffset });
    }
  }

  async irParaPagina(pagina: number): Promise<void> {
    const limit = this.$paginacao().limit ?? 20;
    const novoOffset = (pagina - 1) * limit;
    await this.carregarUsers({ offSet: novoOffset });
  }

  async alterarLimit(novoLimit: number): Promise<void> {
    await this.carregarUsers({ offSet: 0, limit: novoLimit });
  }

  async alterarOrdenacao(campo: string, direcao: 1 | -1): Promise<void> {
    this._paginacao.update((current) => ({
      ...current,
      param: campo,
      ordenacao: direcao,
    }));
    await this.carregarUsers({ offSet: 0 });
  }

  async buscar(nome?: string, email?: string): Promise<void> {
    this.setFiltros({ nome, email });
    await this.carregarUsers({ offSet: 0 }); // Voltar para primeira página
  }
}
